/**
 * Utility functions and constants for the Deliveries page
 */

// Statuses to hide by default
export const HIDDEN_STATUSES = ['Delivered', 'Archive', 'Archived', 'Canceled'];

// Default "normal" items that don't need attention
export const DEFAULT_NORMAL_ITEMS = [
  "extended frame warranty", "kandi wheels", "wheel spacers", "hitch receiver",
  "floor mats", "seat covers", "rear view mirror", "horn", "usb charger", "cup holder"
];

// Custom item status options
export const CUSTOM_ITEM_STATUSES = {
  pending: { label: "Not Ordered", color: "#FF3B30" },
  ordered: { label: "Ordered", color: "#FF9500" },
  ready: { label: "Ready", color: "#39FF14" },
  installed: { label: "Installed", color: "#007AFF" }
};

// Format date as "M-D-YY"
export const formatDateShort = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    return `${date.getMonth() + 1}-${date.getDate()}-${String(date.getFullYear()).slice(-2)}`;
  } catch { return dateStr; }
};

// Calculate age in days
export const calculateAge = (createdDate) => {
  if (!createdDate) return '-';
  try {
    // Handle both date-only ("2026-02-03") and ISO ("2026-02-03T03:15:12.000Z") formats
    const dateStr = createdDate.includes('T') ? createdDate.split('T')[0] : createdDate;
    const created = new Date(dateStr + 'T00:00:00');
    if (isNaN(created.getTime())) return '-';
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.floor((today - created) / (1000 * 60 * 60 * 24));
  } catch { return '-'; }
};

// Fuzzy matching helper
// Strip prices, parens, and dollar amounts to get the core item name
const stripPricing = (text) => text.replace(/\+?\$[\d,.]+/g, '').replace(/\([^)]*\)/g, '').replace(/[+,]/g, ' ').replace(/\s+/g, ' ').trim();

export const fuzzyMatch = (itemText, normalKeywords) => {
  const itemNormalized = itemText.toLowerCase().trim().replace(/\s+/g, ' ');
  const itemStripped = stripPricing(itemNormalized);
  for (const keyword of normalKeywords) {
    const kn = keyword.toLowerCase().trim();
    // Direct include
    if (itemNormalized.includes(kn)) return true;
    // Stripped price comparison
    const knStripped = stripPricing(kn);
    if (knStripped && itemStripped.includes(knStripped)) return true;
    if (knStripped && knStripped.includes(itemStripped) && itemStripped.length > 4) return true;
    // Plural handling
    const ks = knStripped.replace(/s$/, '').replace(/es$/, '');
    const is = itemStripped.replace(/s$/, '').replace(/es$/, '');
    if (ks && ks.length > 3 && is.includes(ks)) return true;
    if (is && is.length > 3 && ks.includes(is)) return true;
    // Word overlap — match if ALL significant words from the keyword appear in the item
    const kw = new Set(knStripped.split(' ').filter(w => w.length > 2));
    const iw = new Set(itemStripped.split(' ').filter(w => w.length > 2));
    if (kw.size > 1 && [...kw].every(k => [...iw].some(i => i.includes(k) || k.includes(i)))) return true;
    // Reverse: if all significant item words appear in the keyword (handles "Underglow" matching "led underglow")
    if (iw.size >= 1 && [...iw].every(i => [...kw].some(k => k.includes(i) || i.includes(k)))) return true;
  }
  return false;
};

export const getStatusColor = (status) => {
  const colors = {
    'Delivered': '#39FF14', 'Out for Delivery': '#007AFF', 'Ready for Delivery': '#FFD700',
    'Pending': '#FF9500', 'Scheduled': '#8B5CF6', 'Canceled': '#FF3B30', 'On Hold': '#71717A',
    'In Queue': '#007AFF', 'In Progress': '#FFD700', 'Waiting on Cart': '#FF9500',
    'Waiting on parts': '#FF9500', 'Later Delivery Date': '#8B5CF6'
  };
  return colors[status] || '#A1A1AA';
};

export const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try { return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return dateStr; }
};

// Extract custom items from accessories
export const getCustomItems = (accessories, normalItems) => {
  if (!accessories) return [];
  return accessories.split(/[\n,]/).map(l => l.trim()).filter(Boolean)
    .filter(line => !fuzzyMatch(line, normalItems) && line.length > 3);
};

// Check if all custom items are "ready" or "ignored"
export const allCustomItemsReady = (delivery, normalItems) => {
  const customItems = getCustomItems(delivery.accessories, normalItems);
  if (customItems.length === 0) return true;
  return customItems.every(item => {
    const s = delivery.custom_item_statuses?.[item];
    return s === 'ready' || s === 'ignored' || s === 'installed';
  });
};

// Florida area codes
const FL_AREA_CODES = ['239','305','321','352','386','407','561','689','727','754','772','786','813','850','863','904','941','954'];

// Detect if an address is in Florida (with phone fallback for empty addresses)
export const isFloridaAddress = (address, phone) => {
  if (address) {
    const upper = address.toUpperCase();
    if (/\bFL\b|\bFLORIDA\b|\bFL\.\b/.test(upper)) return true;
    if (/\b3[234]\d{3}\b/.test(address)) return true;
  }
  // Fallback: check phone area code when no address
  if (!address && phone) {
    const digits = phone.replace(/\D/g, '');
    const area = digits.startsWith('1') ? digits.substring(1, 4) : digits.substring(0, 3);
    if (FL_AREA_CODES.includes(area)) return true;
  }
  return false;
};

// iter 419 — Jake: "Eddie Small is in OOS but should be in Florida deliveries".
// The CRM's State column is the source of truth when it's filled in; the
// address / area-code sniffing is only the fallback (Eddie had no address yet
// and a non-FL area code, so he landed in OOS).
export const isFloridaDelivery = (d) => {
  const st = String(d?.state || "").trim().toUpperCase();
  if (st) return st === "FL" || st === "FLORIDA";
  return isFloridaAddress(d?.customer_address || d?.delivery_address || d?.new_address, d?.customer_phone || d?.phone);
};

// Group deliveries into sections
export const DELIVERY_SECTIONS = [
  { key: 'florida', label: 'Florida Deliveries', color: '#39FF14' },
  { key: 'outOfState', label: 'Out-of-State Deliveries', color: '#007AFF' },
  { key: 'waitingPartsCarts', label: 'Waiting on Parts / Carts', color: '#FF9500' },
  { key: 'laterDeliveryDate', label: 'Later Delivery Date', color: '#8B5CF6' },
  { key: 'outForDelivery', label: 'Out for Delivery', color: '#00BCD4' },
];

export const groupDeliveries = (deliveries) => {
  const groups = { florida: [], outOfState: [], waitingPartsCarts: [], laterDeliveryDate: [], outForDelivery: [] };
  deliveries.forEach(d => {
    if (d.status === 'Out for Delivery') groups.outForDelivery.push(d);
    else if (d.status === 'Later Delivery Date') groups.laterDeliveryDate.push(d);
    else if (d.status === 'Waiting on Cart' || d.status === 'Waiting on parts') groups.waitingPartsCarts.push(d);
    else if (isFloridaDelivery(d)) groups.florida.push(d);
    else groups.outOfState.push(d);
  });
  return groups;
};

// Get deliveries that have been "Out for Delivery" for 3+ days
export const getStaleOutForDelivery = (deliveries) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return deliveries.filter(d => {
    if (d.status !== 'Out for Delivery') return false;
    const deliveryDate = d.delivery_date ? new Date(d.delivery_date + 'T00:00:00') : null;
    if (!deliveryDate) return false;
    const daysDiff = Math.floor((now - deliveryDate) / (1000 * 60 * 60 * 24));
    return daysDiff >= 3;
  });
};
